/**
 * Configuration SuperTokens Frontend SDK
 *
 * Le SDK gère automatiquement :
 * - Interception des requêtes fetch pour ajouter credentials
 * - Refresh automatique des tokens sur 401
 * - Refresh proactif avant expiration
 * - Gestion des cookies httpOnly
 * - Synchronisation multi-onglets
 */

import SuperTokens from 'supertokens-web-js';
import EmailPassword from 'supertokens-web-js/recipe/emailpassword';
import Session from 'supertokens-web-js/recipe/session';
import { NINA_API_URL } from '~/config/api.config';

/**
 * Initialise SuperTokens frontend SDK
 * Doit être appelé une seule fois au démarrage de l'app (client-side only)
 */
export function initSuperTokens() {
  // Ne pas initialiser côté serveur (SSR)
  if (typeof window === 'undefined') {
    return;
  }

  SuperTokens.init({
    appInfo: {
      appName: 'Nina.fm Mixtaper',
      apiDomain: NINA_API_URL,
      apiBasePath: '/auth',
    },
    recipeList: [
      EmailPassword.init(),
      Session.init({
        tokenTransferMethod: 'cookie',
      }),
    ],
  });
}

/**
 * Vérifie si une session existe
 */
export async function doesSessionExist(): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  return Session.doesSessionExist();
}

/**
 * Récupère l'ID utilisateur de la session courante
 */
export async function getUserId(): Promise<string | undefined> {
  if (typeof window === 'undefined') return undefined;
  return Session.getUserId();
}

/**
 * Récupère le payload du token d'accès
 */
export async function getAccessTokenPayload(): Promise<Record<string, unknown> | undefined> {
  if (typeof window === 'undefined') return undefined;
  const payload = await Session.getAccessTokenPayloadSecurely();
  return payload as Record<string, unknown>;
}

/**
 * Force un refresh de la session
 */
export async function attemptRefreshingSession(): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  return Session.attemptRefreshingSession();
}

/**
 * Déconnecte l'utilisateur (révoque la session)
 */
export async function signOut(): Promise<void> {
  if (typeof window === 'undefined') return;
  await Session.signOut();
}
